import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { DeliverablesDashboard } from '@/components/deliverables/DeliverablesDashboard';
import { MyWorkView } from '@/components/views/MyWorkView';
import type { UserView } from '@/lib/schemas';
import type { DeliverableDashboardFilters } from '@/hooks/useDeliverables';

interface DeliverableViewRendererProps {
  view: UserView;
}

type FilterTab = 'all' | 'needs_attention' | 'in_progress' | 'in_review' | 'approved';
type ViewMode = 'list' | 'board' | 'timeline';

const VALID_TABS: FilterTab[] = ['all', 'needs_attention', 'in_progress', 'in_review', 'approved'];
const VALID_MODES: ViewMode[] = ['list', 'board', 'timeline'];

export function DeliverableViewRenderer({ view }: DeliverableViewRendererProps) {
  const config = view.config;
  const filtersCfg = config.filters || {};

  // Client-side tab default
  const tab = VALID_TABS.includes(filtersCfg.tab as FilterTab)
    ? (filtersCfg.tab as FilterTab)
    : 'all';

  const mode = VALID_MODES.includes(filtersCfg.view_mode as ViewMode)
    ? (filtersCfg.view_mode as ViewMode)
    : 'list';

  // Server-side person filters — these actually change the API query
  const apiFilters: DeliverableDashboardFilters = {};
  if (filtersCfg.assigned_to) apiFilters.assigned_to = filtersCfg.assigned_to;
  if (filtersCfg.reviewer) apiFilters.reviewer = filtersCfg.reviewer;
  const hasApiFilters = Object.keys(apiFilters).length > 0;

  // Person-filtered views get the focused MyWorkView
  if (hasApiFilters) {
    return (
      <DashboardLayout title={view.name}>
        <MyWorkView
          personName={(filtersCfg.assigned_to || filtersCfg.reviewer || '') as string}
          description={filtersCfg.description as string | undefined}
          filters={apiFilters}
        />
      </DashboardLayout>
    );
  }

  // Non-person views (PM Daily Check, etc.) get the full dashboard
  return (
    <DashboardLayout title={view.name}>
      <div className="space-y-1 mb-6">
        <p className="text-sm text-muted-foreground">
          {filtersCfg.description || 'Filtered deliverable view.'}
        </p>
      </div>
      <DeliverablesDashboard
        initialFilter={tab}
        initialViewMode={mode}
      />
    </DashboardLayout>
  );
}
